import { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import "../styles/grouped-actions.css";

const GroupedActionMenu = ({
  groups,
  footerActions = [],
  searchable = false,
  className = "",
}) => {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase();

  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          actions: group.actions.filter((action) =>
            action.label.toLocaleLowerCase().includes(normalizedQuery),
          ),
        }))
        .filter((group) => group.actions.length > 0),
    [groups, normalizedQuery],
  );

  const handleAction = (action) => {
    if (action.disabled) return;
    action.onSelect?.();
    setShow(false);
  };

  return (
    <Dropdown
      show={show}
      onToggle={setShow}
      autoClose="outside"
      className={`grouped-action-dropdown ${className}`}
    >
      <Dropdown.Toggle
        as={Button}
        type="button"
        size="sm"
        className="grouped-action-toggle"
        aria-label="More actions"
      >
        More <ChevronDown size={15} aria-hidden="true" />
      </Dropdown.Toggle>
      <Dropdown.Menu
        align="end"
        className="grouped-action-menu"
        popperConfig={{ strategy: "fixed" }}
        renderOnMount
      >
        {searchable && (
          <div
            className="grouped-action-search"
            onClick={(event) => event.stopPropagation()}
          >
            <Search size={16} aria-hidden="true" />
            <Form.Control
              type="search"
              value={query}
              placeholder="Search actions"
              aria-label="Search actions"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setShow(false);
              }}
            />
          </div>
        )}

        {filteredGroups.map((group) => (
          <section className="grouped-action-section" key={group.label}>
            <div className="grouped-action-heading">{group.label}</div>
            {group.actions.map((action) => (
              <Dropdown.Item
                as="button"
                type="button"
                key={action.label}
                disabled={action.disabled}
                className={`grouped-action-item${action.danger ? " is-danger" : ""}${action.highlighted ? " is-highlighted" : ""}`}
                onClick={() => handleAction(action)}
              >
                <span className="grouped-action-icon" aria-hidden="true">
                  {action.icon}
                </span>
                <span className="grouped-action-copy">
                  <span className="grouped-action-label">{action.label}</span>
                  {action.description && (
                    <span className="grouped-action-description">
                      {action.description}
                    </span>
                  )}
                </span>
                {action.badge && (
                  <span className="grouped-action-badge">{action.badge}</span>
                )}
              </Dropdown.Item>
            ))}
          </section>
        ))}

        {searchable && filteredGroups.length === 0 && (
          <div className="grouped-action-empty">No actions found</div>
        )}

        {footerActions.length > 0 && (
          <>
            <div className="grouped-action-divider" />
            {footerActions.map((action) => (
              <Dropdown.Item
                as="button"
                type="button"
                key={action.label}
                disabled={action.disabled}
                className={`grouped-action-item${action.danger ? " is-danger" : ""}${action.highlighted ? " is-highlighted" : ""}`}
                onClick={() => handleAction(action)}
              >
                <span className="grouped-action-icon" aria-hidden="true">
                  {action.icon}
                </span>
                <span className="grouped-action-copy">
                  <span className="grouped-action-label">{action.label}</span>
                  {action.description && (
                    <span className="grouped-action-description">
                      {action.description}
                    </span>
                  )}
                </span>
                {action.badge && (
                  <span className="grouped-action-badge">{action.badge}</span>
                )}
              </Dropdown.Item>
            ))}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default GroupedActionMenu;
